import React, { FC, useEffect, useRef } from 'react';
import { Entry } from 'contentful';
import { SwiperCarousel, SwiperSlide } from './Swiper';
import contentfulComponentLoop from '../lib/contentfulComponentLoop';
import { Navigation, Pagination, Mousewheel, Keyboard } from 'swiper/modules';
import styled from 'styled-components';

interface CarouselProps extends Entry {
  fields: {
    autoheight: boolean;
    autoplay?: number;
    centeredSlides: boolean;
    desktopSlidesPerView: number;
    desktopSpaceBetween: number;
    loop: boolean;
    mobileSlidesPerView: number;
    mobileSpaceBetween: number;
    peekHalfTiles: boolean;
    slides: Entry[];
    tabletSlidesPerView: number;
    tabletSpaceBetween: number;
    theme: string;
    forceFullWidth: boolean;
    freeMode: boolean;
    sticky: boolean;
    activeSlideStyling?: string;
    navigationArrows: boolean;
    paginationType: string;
    carouselStyle: string;
  };
  maxWidth?: number;
  onForceFullWidthChange?: (forceFullWidth: boolean) => void;
  parentProps?: any;
  brand?: string;
}

const CarouselWrapper = styled.div<{
  $activeSlideStyling?: string;
  $carouselStyle?: string;
  $forceFullWidth: boolean;
  $maxWidth?: number;
  $navigationArrows: boolean;
  $paginationType: string;
  $brand?: string;
}>`
  display: flex;
  position: relative;
  padding: 0;
  overflow: ${({ $navigationArrows, $paginationType }) =>
    $navigationArrows || $paginationType !== 'None' ? 'visible' : 'hidden'};
  width: auto;
  max-width: 100%;
  margin-left: initial;
  margin-right: initial;

  .swiper-container {
    max-width: 100%;
    box-sizing: border-box;
    overflow: visible;

    .swiper-slide-active {
      transition: all 0.3s ease-in-out;
    }
  }

  ${({ $forceFullWidth, $maxWidth }) => $forceFullWidth ? `
    overflow: visible;
    width: calc(100vw - 16px);
    max-width: calc(100vw - 16px);
    margin-left: ${$maxWidth ? `calc((100vw - ${$maxWidth}px) / 2 * -1 + 16px)` : 'calc((100vw - 100%) / 2 * -1 + 16px)'};
    padding-left: 16px;
  ` : `
    margin-right: initial;
    margin-left: initial;
    max-width: ${$maxWidth ? `${$maxWidth}px` : '100%'};
  `}

  .swiper-pagination {
    justify-content: center;
    margin-bottom: -70px;

    .swiper-pagination-bullet,
    .swiper-pagination-bullet-prev,
    .swiper-pagination-bullet-prev-prev,
    .swiper-pagination-bullet-next,
    .swiper-pagination-bullet-next-next {
      flex-shrink: 0;
      width: 4px;
      height: 4px;
      border-radius: 100px;
      margin: 0 5px;
      cursor: pointer;
      border: 2px solid #3F4040;
      background: #3F4040;
      opacity: 1;
      transform: scale(1);
    }

    .swiper-pagination-bullet-active {
      width: 4px;
      height: 10px;
      border: 2px solid #0083FF;
      background: #0083FF;
      opacity: 1;
      transform: scale(1);
    }

    .swiper-pagination-progressbar-fill {
      background: #0083FF;
    }

    .swiper-pagination-fraction {
      font-size: 1rem;
    }
  }

  .swiper-navigation {
    display: none;
    pointer-events: none;

    @media (min-width: 768px) {
      display: block;
    }

    .swiper-button-next,
    .swiper-button-prev {
      display: flex;
      color: #000;
      background: #fff;
      border-radius: 50%;
      width: 36px;
      height: 36px;
      pointer-events: all;
      position: absolute;
      z-index: 10;

      ${({ $brand }) =>
        $brand == "jackson" &&
        `
        background: transparent;
      `}
    }

    .swiper-button-next {
      right: 40px;
      &::after {
        content: url('https://www.fmicassets.com/sites/presonus.com/svg/arrow-right.svg');
      }
    }

    .swiper-button-prev {
      left: 24px;
      &::after {
        content: url('https://www.fmicassets.com/sites/presonus.com/svg/arrow-left.svg');
      }
    }
  }

  ${({ $activeSlideStyling }) => {
    switch ($activeSlideStyling) {
      case 'Scale Down Inactive':
        return `
          .swiper-slide-prev,
          .swiper-slide-next {
            transform: scale(0.8);
          }
        `;
        case 'Full Content':
          return `
            swiper-slide,
            .swiper-slide-prev,
            .swiper-slide-next {
              transform: scale(0.8);

              h1, h2, h3, h4, h5, p {
                display: none;
              }
        
              .hide-on-inactive {
                opacity: 0;
                pointer-events: none;
                transition: opacity 0.3s ease-in-out;
              }
            }
        
            .swiper-slide-active {
              transform: scale(1);

              .hide-on-inactive {
                opacity: 1;
                pointer-events: auto;
                transition: opacity 0.3s ease-in-out;
              }
            }
          `;
      default:
        return '';
    }
  }}

  ${({ $carouselStyle }) => {
    switch ($carouselStyle) {
      case 'Hero Carousel':
        return `
          width: 100vw;
          max-width: 100vw;
          margin-left: 0;
          padding-left: 0px;
        `;
      case 'Product Carousel':
        return `
          .swiper-navigation {
            position: absolute;
            top: -32px;
            right: 16px;
            z-index: 10;

            .swiper-button-prev {
              left: auto; 
              right: 48px;
            }

            .swiper-button-next {
              right: 0;
            }
          }
        `;
      case 'Product Highlight':
        return `
          .swiper-navigation {
            background: transparent;
            position: sticky;
            z-index: 10;
              
            .swiper-button-prev {
              left: auto;
              right: 30px;
            }

            .swiper-button-next {
              left: auto;
              right: 0px;
            }
          }
        `;
      default:
        return '';
    }
  }}
`;

const Carousel: FC<CarouselProps> = ({
  sys,
  fields: {
    autoheight,
    autoplay,
    centeredSlides,
    desktopSlidesPerView,
    desktopSpaceBetween,
    loop,
    mobileSlidesPerView,
    mobileSpaceBetween,
    peekHalfTiles,
    slides,
    tabletSlidesPerView,
    tabletSpaceBetween,
    theme,
    freeMode,
    sticky,
    forceFullWidth,
    activeSlideStyling,
    navigationArrows,
    paginationType,
    carouselStyle,
  },
  maxWidth,
  onForceFullWidthChange,
  brand,
  parentProps,
}) => {
  const swiperRef = useRef<any>(null);

  useEffect(() => {
    if (onForceFullWidthChange) {
      onForceFullWidthChange(forceFullWidth);
    }

    const swiperInstance = swiperRef.current?.swiper;

    return () => {
      if (swiperInstance) {
        swiperInstance.off('slideChange');
      }
    };
  }, [forceFullWidth, onForceFullWidthChange]);

  const breakpoints = {
    768: {
      slidesPerView: peekHalfTiles ? tabletSlidesPerView + 0.5 : tabletSlidesPerView,
      spaceBetween: tabletSpaceBetween
    },
    1024: {
      slidesPerView: peekHalfTiles ? desktopSlidesPerView + 0.5 : desktopSlidesPerView,
      spaceBetween: desktopSpaceBetween
    },
  };

  const convertPaginationType = (type: string | undefined) => {
    switch (type) {
      case 'Bullets':
        return 'bullets';
      case 'Progress Bar':
        return 'progressbar';
      case 'Fraction':
        return 'fraction';
      case 'Custom':
        return 'custom';
      case 'None':
      default:
        return 'none';
    }
  };

  const renderPagination = () => {
    const paginationTypeConverted = convertPaginationType(paginationType);
    
    if (paginationTypeConverted === 'none') {
      return false;
    }

    return {
      el: `.swiper-pagination-${sys.id}`,
      type: paginationTypeConverted,
      clickable: true,
      dynamicBullets: paginationTypeConverted == 'bullets',
    };
  };

  return (
    <CarouselWrapper
      $forceFullWidth={forceFullWidth}
      $activeSlideStyling={activeSlideStyling}
      $maxWidth={forceFullWidth ? undefined : maxWidth}
      $carouselStyle={carouselStyle}
      $navigationArrows={navigationArrows}
      $paginationType={paginationType}
      $brand={brand}
    >
      <SwiperCarousel
        ref={swiperRef}
        modules={[Navigation, Pagination, Mousewheel, Keyboard]}
        autoHeight={autoheight}
        autoplay={autoplay && autoplay > 0 ? { delay: autoplay } : false}
        breakpoints={breakpoints}
        centeredSlides={centeredSlides}
        freeMode={freeMode}
        sticky={sticky}
        keyboard={{
          enabled: true,
          onlyInViewport: true
        }}
        loop={loop}
        mousewheel={{ forceToAxis: true }}
        slidesPerView={peekHalfTiles ? mobileSlidesPerView + 0.5 : mobileSlidesPerView}
        spaceBetween={mobileSpaceBetween}
        navigation={
          navigationArrows
            ? {
                nextEl: `.swiper-button-next-${sys.id}`,
                prevEl: `.swiper-button-prev-${sys.id}`,
              }
            : false
        }
        pagination={renderPagination()}
        className="swiper-container"
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={`${slide.sys.id}-${index}`}>
            {contentfulComponentLoop([slide], {
              parentComponentKey: 'carousel',
              carouselTheme: theme,
              carouselStyle: carouselStyle,
              activeSlideStyling: activeSlideStyling,
              totalSlides: slides.length,
              navigation: navigationArrows && carouselStyle === 'Product Highlight' && (
                <div className="swiper-navigation">
                  <div className={`swiper-button-prev swiper-button-prev-${sys.id}`}></div>
                  <div className={`swiper-button-next swiper-button-next-${sys.id}`}></div>
                </div>
              ),
              ...parentProps,
            })}
          </SwiperSlide>
        ))}
      </SwiperCarousel>
      {carouselStyle !== 'Product Highlight' && (
        <>
          {navigationArrows && (
            <div className="swiper-navigation">
              <div className={`swiper-button-prev swiper-button-prev-${sys.id}`}></div>
              <div className={`swiper-button-next swiper-button-next-${sys.id}`}></div>
            </div>
          )}
          {paginationType && paginationType !== 'None' && (
            <div className={`swiper-pagination swiper-pagination-${sys.id}`}></div>
          )}
        </>
      )}
    </CarouselWrapper>
  );
};

export default Carousel;